'use memo';

import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { z } from 'zod';
import ActionButton from '@/components/Button/ActionButton';
import { Text } from '@/components/Typography';
import { useToast } from '@/components/ui/use-toast';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';
import { defaultNS, type KeyPrefixSelector, useTranslation } from '@/lib/i18n';
import { useSequenceSidebarData } from '@/routes/_chat/chat/$sessionId/-query';
import { IndividualSidebar } from '../../-components/IndividualSidebar';
import { MY_CLOCKING_EMPLOYEE_ID } from '../lookup/-hooks/useMyEmployeeId';
import { CLOCKING_CORRECTION_ORG_ID, getCorrectableLatestPeriod } from './-api';
import { ClockingCorrectionBulkBar } from './-components/ClockingCorrectionBulkBar';
import { ClockingCorrectionFilterBar } from './-components/ClockingCorrectionFilterBar';
import { ClockingCorrectionPeriodToolbar } from './-components/ClockingCorrectionPeriodToolbar';
import { ClockingCorrectionSubmitDialog } from './-components/ClockingCorrectionSubmitDialog';
import { ClockingCorrectionTable } from './-components/ClockingCorrectionTable';
import { useClockingCorrectionEditor } from './-hooks/useClockingCorrectionEditor';
import { useClockingCorrectionRows, useSubmitClockingCorrections } from './-query';
import type { ClockingCorrectionRow } from './-types';
import { buildCorrectionRecords } from './-utils/buildCorrectionRecords';

// 取得前の安定参照。毎レンダーで `?? []` を返すと editor の useEffect([rows]) が連発するため。
const EMPTY_ROWS: ClockingCorrectionRow[] = [];
const pageKeyPrefix = ($ => $.individualPages.clockingCorrection) satisfies KeyPrefixSelector<typeof defaultNS>;
const SearchSchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  // 打刻漏れカードからの遷移で ON にする。URL では "true"。
  missingOnly: z.union([z.boolean(), z.string()]).transform(v => v === true || v === 'true').optional()
});
export const Route = createFileRoute('/_individual/individual/portal/clocking-correction/')({
  validateSearch: zodValidator(SearchSchema),
  beforeLoad: async ({
    search
  }) => {
    if (search.year != null && search.month != null) return;
    // DB(IndexedDB) 障害時は期間不明として扱い、全画面エラーへ落とさない。
    const latest = await getCorrectableLatestPeriod(MY_CLOCKING_EMPLOYEE_ID).catch(() => null);
    if (!latest) return;
    throw redirect({
      to: '/individual/portal/clocking-correction',
      search: {
        ...search,
        year: latest.year,
        month: latest.month
      },
      replace: true
    });
  },
  component: ClockingCorrectionPage
});
function ClockingCorrectionPage() {
  const employeeId = MY_CLOCKING_EMPLOYEE_ID;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    toast
  } = useToast();
  const {
    isMobile
  } = useDesktopOrMobileMediaQuery();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: pageKeyPrefix
  });
  const sidebarData = useSequenceSidebarData();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const year = search.year;
  const month = search.month;
  const missingOnly = search.missingOnly ?? false;
  const canQuery = year != null && month != null;
  const {
    data: rows,
    isPending
  } = useClockingCorrectionRows(employeeId, year ?? 0, month ?? 0, {
    enabled: canQuery
  });
  const editor = useClockingCorrectionEditor(rows ?? EMPTY_ROWS, isPending, missingOnly);
  const submitMutation = useSubmitClockingCorrections(employeeId, year ?? 0, month ?? 0);
  const goToMonth = (offset: number) => {
    if (!canQuery || year == null || month == null) return;
    const d = new Date(year, month - 1 + offset);
    void navigate({
      to: '.',
      search: s => ({
        ...s,
        year: d.getFullYear(),
        month: d.getMonth() + 1
      })
    });
  };
  const setMissingOnly = (value: boolean) => {
    void navigate({
      to: '.',
      search: s => ({
        ...s,
        missingOnly: value ? true : undefined
      })
    });
  };
  const handleSubmit = async (reason: string) => {
    if (editor.editedRows.length === 0) return;
    const records = buildCorrectionRecords({
      employeeId,
      organizationId: CLOCKING_CORRECTION_ORG_ID,
      editedRows: editor.editedRows,
      drafts: editor.drafts,
      reason
    });
    try {
      await submitMutation.mutateAsync(records);
      toast({
        title: t($ => $.submitSuccessTitle),
        description: t($ => $.submitSuccessDescription, {
          num: records.length
        })
      });
      setSubmitDialogOpen(false);
      editor.clearSelection();
    } catch {
      toast({
        variant: 'destructive',
        title: t($ => $.submitErrorTitle),
        description: t($ => $.submitErrorDescription)
      });
    }
  };
  const editedCount = editor.editedRows.length;
  const hasRows = (rows?.length ?? 0) > 0;
  const hasSelection = editor.selectedRowKeys.length > 0;
  return <div className="flex h-full min-h-0 w-full overflow-hidden">
      <IndividualSidebar mode="portal" open={sidebarOpen} onToggle={() => setSidebarOpen(v => !v)} recentSessions={sidebarData.data?.recentSessions ?? []} upcomingInteractions={sidebarData.data?.upcomingInteractions ?? []} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-neutral-bottom-fill">
        <div className="flex min-h-0 flex-1 flex-col gap-5 px-4 py-6 md:px-12 md:py-8">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-neutral-muted-border pb-5">
            <div className="flex flex-col gap-1">
              <Text as="h1" variant="h2">
                {t($ => $.title)}
              </Text>
              <Text variant="p" className="text-neutral-muted-on-fill">
                {t($ => $.description)}
              </Text>
            </div>
            {canQuery && year != null && month != null ? <ClockingCorrectionPeriodToolbar year={year} month={month} onPrev={() => goToMonth(-1)} onNext={() => goToMonth(1)} /> : null}
          </div>

          {!canQuery ? <Text variant="p" className="text-neutral-muted-on-fill">
              {t($ => $.empty)}
            </Text> : isPending ? <div className="min-h-0 flex-1 animate-pulse rounded-lg bg-neutral-muted-fill" /> : !hasRows ? <Text variant="p" className="text-neutral-muted-on-fill">
              {t($ => $.emptyPeriod)}
            </Text> : <>
              <ClockingCorrectionFilterBar missingOnly={missingOnly} missingPunchCount={editor.missingPunchCount} onChange={setMissingOnly} />
              {missingOnly && editor.missingPunchCount === 0 ? <Text variant="p" className="text-neutral-muted-on-fill">
                  {t($ => $.emptyMissing)}
                </Text> : <div className="relative flex min-h-0 flex-1 flex-col">
                  <ClockingCorrectionTable table={editor.table} displayState={editor.displayState} editApi={editor.editApi} bottomInsetClassName={hasSelection ? isMobile ? 'pb-96' : 'pb-32' : undefined} />
                  {hasSelection ? <ClockingCorrectionBulkBar selectedCount={editor.selectedRowKeys.length} onApply={editor.applyToSelected} onClearSelection={editor.clearSelection} /> : null}
                </div>}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Text variant="label" className="text-neutral-on-fill">
                  {t($ => $.editedCount, {
                num: editedCount
              })}
                </Text>
                <ActionButton type="button" variant="default" disabled={editedCount === 0} onClick={() => setSubmitDialogOpen(true)}>
                  {t($ => $.batchSubmit)}
                </ActionButton>
              </div>
            </>}
        </div>
      </main>

      <ClockingCorrectionSubmitDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen} count={editedCount} isSubmitting={submitMutation.isPending} onConfirm={reason => void handleSubmit(reason)} />
    </div>;
}